import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { keyframes } from '@emotion/css';

import { Gavi } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { color, movimiento, radio } from '#components/mobile/ui/tokens';
import { useReducedMotion } from '#hooks/useReducedMotion';
import { useEstadoSiHay } from '#redux';

import { brujulaAbierta, llevarAEscena } from './escenas';
import { GatilloFantasma } from './GatilloFantasma';
import {
  ampliarCaja,
  cajaDe,
  cajaVisible,
  colocarBocadillo,
  rutaConHueco,
  rutaFlecha,
  unirCajas,
} from './geometria';
import type { Caja } from './geometria';
import { LATON } from './OjoBuey';
import { posicionPaso, preguntaDisponible, siguientePaso } from './pasos';
import type { PasoAyuda, PreguntaAyuda } from './pasos';
import { conAvisoProblema } from './preguntaProblema';

/** Por encima de las hojas (205) y de la brújula (102). */
export const Z_AYUDA = 3000;

const ANCHO_BOCADILLO = 362;
const MARGEN_FOCO = 6;

const latido = keyframes({
  '50%': {
    boxShadow: `0 0 0 2.5px ${LATON.medio}, 0 0 0 9px rgba(201,151,59,.16), 0 0 30px 8px rgba(242,211,138,.6)`,
  },
});
const dibuja = keyframes({
  from: { opacity: 0, strokeDashoffset: 60 },
  to: { opacity: 1, strokeDashoffset: 0 },
});
const aparece = keyframes({
  from: { opacity: 0 },
});
const entra = keyframes({ from: { opacity: 0 } });
const toque = keyframes({
  '0%': { transform: 'scale(1.5)', opacity: 0 },
  '30%': { transform: 'scale(1)', opacity: 1 },
  '55%': {
    transform: 'scale(.78)',
    boxShadow: '0 0 0 0 rgba(201,151,59,.6)',
  },
  '80%': {
    transform: 'scale(1)',
    boxShadow: '0 0 0 16px rgba(201,151,59,0)',
    opacity: 1,
  },
  '100%': { opacity: 0 },
});

type Medida = {
  ancho: number;
  alto: number;
  foco: Caja | null;
  etiquetas: Array<{ x: number; y: number; debajo: boolean; texto: string }>;
  altoBocadillo: number;
};

type AyudaGaviProps = {
  /** Nombre de la pantalla en la cabecera del bocadillo («Movimientos»). */
  pantalla: string;
  /** La primera, el recorrido entero; luego, las preguntas. */
  preguntas: PreguntaAyuda[];
  /**
   * Al salir de una pregunta (cerrar o volver a la lista), tras dejar las
   * escenas en normal: la pantalla quita lo que abrió de ejemplo.
   */
  alSalir?: () => void;
  onCerrar: () => void;
};

/**
 * Ayuda con Gavi sobre la pantalla de verdad. Abre con «¿Cómo puedo
 * ayudarte?» y una lista: el recorrido entero de la pantalla o una pregunta
 * con solo sus pasos (Vadik, 26-oct-2026: que no haya que tragarse todo el
 * tutorial cada vez). Las preguntas sin nada que enseñar ahora no salen.
 */
export function AyudaGavi({
  pantalla,
  preguntas,
  alSalir,
  onCerrar,
}: AyudaGaviProps) {
  const { t } = useTranslation();
  // «Avisar de un problema aquí» (en la brújula solo con cuenta).
  const userData = useEstadoSiHay()?.user.data;
  const conCuenta = Boolean(userData?.userId) && !userData?.offline;
  const [visibles] = useState(() =>
    conAvisoProblema(
      preguntas,
      {
        tocaBrujula: t('Tap the compass, at the bottom right.'),
        explica: t(
          'If something fails on this screen, tell me from here: Rumbo takes the photo and you tell me what is going on. I have spent years eavesdropping in the harbour; this way, at least, you tell me yourself.',
        ),
        pregunta: t('What do I do if something fails?'),
      },
      conCuenta,
    ).filter((p, i) => i === 0 || preguntaDisponible(p)),
  );
  const [elegida, setElegida] = useState<number | null>(null);
  if (elegida == null) {
    return (
      <MenuAyuda
        pantalla={pantalla}
        preguntas={visibles}
        onElegir={setElegida}
        onCerrar={onCerrar}
      />
    );
  }
  const pregunta = visibles[elegida];
  if (pregunta.elegir) {
    return (
      <ElegirYExplicar
        key={pregunta.id}
        pantalla={pantalla}
        elegir={pregunta.elegir}
        onPreguntas={() => setElegida(null)}
        onCerrar={onCerrar}
      />
    );
  }
  return (
    <Recorrido
      key={pregunta.id}
      pantalla={pantalla}
      pasos={pregunta.pasos}
      alSalir={alSalir}
      onPreguntas={() => setElegida(null)}
      onCerrar={onCerrar}
    />
  );
}

/**
 * «Sobre una de mis tarjetas»: primero «toca la que quieras» (laten con una
 * chapa «?», sin mecerse: mecerse es «la puedes mover»); luego Gavi explica
 * la elegida, con «Otra tarjeta» y «Entendido».
 */
function ElegirYExplicar({
  pantalla,
  elegir,
  onPreguntas,
  onCerrar,
}: {
  pantalla: string;
  elegir: NonNullable<PreguntaAyuda['elegir']>;
  onPreguntas: () => void;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const [elegido, setElegido] = useState<Element | null>(null);
  if (!elegido) {
    return (
      <EsperarToque
        pantalla={pantalla}
        elegir={elegir}
        onElegir={setElegido}
        onPreguntas={onPreguntas}
        onCerrar={onCerrar}
      />
    );
  }
  return (
    <Recorrido
      key={elegir.objetivos().indexOf(elegido)}
      pantalla={pantalla}
      pasos={[
        {
          id: 'elegido',
          escena: 'normal',
          final: true,
          texto: elegir.explicar(elegido),
          objetivo: () => (elegido.isConnected ? [elegido] : []),
        },
      ]}
      otra={{ texto: t('Another card'), onPress: () => setElegido(null) }}
      onPreguntas={onPreguntas}
      onCerrar={onCerrar}
    />
  );
}

const latidoElegir = keyframes({
  '50%': {
    boxShadow: `0 0 0 3px rgba(138,100,32,.75), 0 0 18px 2px rgba(242,211,138,.6)`,
  },
});

/** Espera a que se toque uno de los objetivos (la página sigue desplazándose). */
function EsperarToque({
  pantalla,
  elegir,
  onElegir,
  onPreguntas,
  onCerrar,
}: {
  pantalla: string;
  elegir: NonNullable<PreguntaAyuda['elegir']>;
  onElegir: (el: Element) => void;
  onPreguntas: () => void;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const quieta = useReducedMotion();
  const bocadillo = useRef<HTMLDivElement>(null);
  const [cajas, setCajas] = useState<Caja[]>([]);

  // Mide cada fotograma: la página se puede desplazar mientras tanto.
  useEffect(() => {
    let anterior = '';
    let marco = 0;
    const medir = () => {
      const nuevas = elegir.objetivos().map(cajaDe);
      const firma = JSON.stringify(nuevas);
      if (firma !== anterior) {
        anterior = firma;
        setCajas(nuevas);
      }
      marco = window.requestAnimationFrame(medir);
    };
    medir();
    return () => window.cancelAnimationFrame(marco);
  }, [elegir]);

  // Los toques no llegan a la pantalla (no abren nada ni empiezan a
  // personalizar); uno sobre un objetivo lo elige. Desplazar sí funciona.
  useEffect(() => {
    let inicio: { x: number; y: number } | null = null;
    const dentroDelBocadillo = (e: Event) =>
      bocadillo.current?.contains(e.target as Node) ?? false;
    const abajo = (e: PointerEvent) => {
      if (dentroDelBocadillo(e)) {
        return;
      }
      e.stopPropagation();
      inicio = { x: e.clientX, y: e.clientY };
    };
    const arriba = (e: PointerEvent) => {
      if (dentroDelBocadillo(e)) {
        return;
      }
      e.stopPropagation();
      if (
        !inicio ||
        Math.hypot(e.clientX - inicio.x, e.clientY - inicio.y) > 10
      ) {
        return;
      }
      const el = elegir.objetivos().find(o => {
        const c = cajaDe(o);
        return (
          e.clientX >= c.izq &&
          e.clientX <= c.der &&
          e.clientY >= c.arriba &&
          e.clientY <= c.abajo
        );
      });
      if (el) {
        onElegir(el);
      }
    };
    const click = (e: MouseEvent) => {
      if (!dentroDelBocadillo(e)) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCerrar();
      }
    };
    window.addEventListener('pointerdown', abajo, true);
    window.addEventListener('pointerup', arriba, true);
    window.addEventListener('click', click, true);
    window.addEventListener('keydown', tecla, true);
    return () => {
      window.removeEventListener('pointerdown', abajo, true);
      window.removeEventListener('pointerup', arriba, true);
      window.removeEventListener('click', click, true);
      window.removeEventListener('keydown', tecla, true);
    };
  }, [elegir, onElegir, onCerrar]);

  const ancho = window.innerWidth;
  const anchoBoc = Math.min(ANCHO_BOCADILLO, ancho - 28);
  const izqBoc = (ancho - anchoBoc) / 2;

  return createPortal(
    <div
      data-testid="ayuda-gavi"
      data-paso="elegir"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_AYUDA,
        pointerEvents: 'none',
      }}
    >
      {cajas.map((c, i) => (
        <div
          key={i}
          aria-hidden
          data-testid="ayuda-elegible"
          style={{
            position: 'absolute',
            left: c.izq,
            top: c.arriba,
            width: c.der - c.izq,
            height: c.abajo - c.arriba,
            borderRadius: 20,
            boxShadow: '0 0 0 2px rgba(138,100,32,.6)',
            animation: quieta
              ? 'none'
              : `${latidoElegir} 1.6s ease-in-out infinite`,
          }}
        >
          <span
            style={{
              position: 'absolute',
              right: 10,
              top: -9,
              width: 30,
              height: 30,
              borderRadius: '50%',
              color: '#3d2a06',
              font: '800 16px/30px Manrope, sans-serif',
              textAlign: 'center',
              background: `radial-gradient(circle at 35% 30%, ${LATON.claro}, ${LATON.medio} 60%, ${LATON.oscuro})`,
              boxShadow: '0 2px 6px rgba(14,20,36,.3)',
            }}
          >
            ?
          </span>
        </div>
      ))}
      <div
        ref={bocadillo}
        role="dialog"
        aria-label={t('Gavi explains {{screen}}', { screen: pantalla })}
        data-testid="ayuda-bocadillo"
        style={{
          position: 'absolute',
          left: izqBoc,
          bottom: 'calc(24px + env(safe-area-inset-bottom))',
          width: anchoBoc,
          boxSizing: 'border-box',
          backgroundColor: color.surface,
          color: color.fg,
          borderRadius: 20,
          boxShadow: `0 14px 34px rgba(14,20,36,.28), 0 0 0 1px rgba(201,151,59,.45)`,
          padding: '14px 14px 12px 16px',
          pointerEvents: 'auto',
          animation: quieta ? 'none' : `${entra} 250ms ease-out`,
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            right: 16,
            top: -52,
            pointerEvents: 'none',
          }}
        >
          <Gavi estado="vuela" clave={0} duracion={750} ancho={62} />
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: LATON.oscuro,
            marginBottom: 4,
          }}
        >
          {t('Gavi · {{screen}}', { screen: pantalla })}
        </div>
        <div
          aria-live="polite"
          style={{ fontSize: 15, lineHeight: 1.45, fontWeight: 600 }}
        >
          {elegir.texto}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 10,
          }}
        >
          <Boton
            variante="fantasma"
            data-testid="ayuda-atras"
            onPress={onPreguntas}
          >
            <Trans>Questions</Trans>
          </Boton>
          <Boton
            variante="fantasma"
            data-testid="ayuda-cerrar"
            onPress={onCerrar}
          >
            <Trans>Close help</Trans>
          </Boton>
        </div>
      </div>
    </div>,
    document.body,
  );
}

type RecorridoProps = {
  pantalla: string;
  pasos: PasoAyuda[];
  /** En el último paso, en vez de «Otra pregunta» (p. ej. «Otra tarjeta»). */
  otra?: { texto: string; onPress: () => void };
  alSalir?: () => void;
  /** Vuelve a la lista de preguntas (con la pantalla ya en normal). */
  onPreguntas: () => void;
  onCerrar: () => void;
};

/**
 * Los pasos de una pregunta: emborrona todo menos lo que explica (aro de
 * latón y flecha), con Atrás, Siguiente y ✕. Lleva la pantalla a cada estado
 * (brújula abierta, varios elegidos, teclado…) tocando sus mismos botones y,
 * al cerrar, la deja como estaba.
 */
function Recorrido({
  pantalla,
  pasos,
  otra,
  alSalir,
  onPreguntas,
  onCerrar,
}: RecorridoProps) {
  const { t } = useTranslation();
  const quieta = useReducedMotion();
  // Qué pasos salen: se decide al abrir, con la pantalla tal cual (los de
  // la pantalla normal, además, solo si lo que enseñan está a la vista; eso
  // se mira ya montada, antes de pintar).
  const aplica = useRef<boolean[]>([]);
  if (aplica.current.length !== pasos.length) {
    aplica.current = pasos.map(p => p.disponible?.() ?? true);
  }
  const [indice, setIndice] = useState(() => {
    const i = siguientePaso(aplica.current, -1, 1);
    return i === 'fuera' ? 0 : i;
  });
  useLayoutEffect(() => {
    aplica.current = pasos.map(
      (p, i) =>
        aplica.current[i] && (p.escena !== 'normal' || p.objetivo().length > 0),
    );
    const i = siguientePaso(aplica.current, -1, 1);
    if (i !== 'fuera') {
      setIndice(i);
    }
    // Solo al abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [listo, setListo] = useState(false);
  // El botón que Gavi «pulsa» antes de abrir lo que enseña (con su dedo).
  const [pulsando, setPulsando] = useState<Caja | null>(null);
  const [medida, setMedida] = useState<Medida | null>(null);
  const direccion = useRef<1 | -1>(1);
  const turno = useRef(0);
  const cerrando = useRef(false);
  const bocadillo = useRef<HTMLDivElement>(null);
  const paso = pasos[indice];

  const cerrar = async (despues = onCerrar) => {
    if (cerrando.current) {
      return;
    }
    cerrando.current = true;
    turno.current++;
    await llevarAEscena('normal');
    alSalir?.();
    despues();
  };

  const ir = (dir: 1 | -1) => {
    const destino = siguientePaso(aplica.current, indice, dir);
    if (destino === 'fuera') {
      void cerrar(dir === 1 ? onCerrar : onPreguntas);
      return;
    }
    direccion.current = dir;
    setIndice(destino);
  };

  // Al entrar en un paso: lleva la pantalla a su escena y comprueba que lo
  // que enseña está; si no, lo salta en la misma dirección.
  useEffect(() => {
    const mio = ++turno.current;
    setListo(false);
    setPulsando(null);
    void (async () => {
      await llevarAEscena(paso.escena);
      if (mio !== turno.current) {
        return;
      }
      const boton = direccion.current === 1 ? paso.pulsa?.() : null;
      if (boton) {
        if (!cajaVisible(cajaDe(boton), window.innerHeight)) {
          boton.scrollIntoView({ block: 'center' });
        }
        setPulsando(
          ampliarCaja(
            cajaDe(boton),
            MARGEN_FOCO,
            window.innerWidth,
            window.innerHeight,
          ),
        );
        await new Promise(r => window.setTimeout(r, quieta ? 400 : 1300));
        if (mio !== turno.current) {
          return;
        }
        setPulsando(null);
      }
      await paso.preparar?.();
      if (mio !== turno.current) {
        return;
      }
      const objetivo = paso.objetivo();
      if (objetivo.length === 0) {
        aplica.current[indice] = false;
        const otro = siguientePaso(aplica.current, indice, direccion.current);
        if (otro === 'fuera') {
          if (direccion.current === 1) {
            void cerrar();
          } else {
            direccion.current = 1;
            const adelante = siguientePaso(aplica.current, indice, 1);
            if (adelante === 'fuera') {
              void cerrar();
            } else {
              setIndice(adelante);
            }
          }
        } else {
          setIndice(otro);
        }
        return;
      }
      if (!cajaVisible(cajaDe(objetivo[0]), window.innerHeight)) {
        objetivo[0].scrollIntoView({ block: 'center' });
      }
      setListo(true);
    })();
    // Solo al cambiar de paso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indice]);

  // Mide cada fotograma (la lista puede moverse, las hojas entran
  // deslizándose) y solo repinta si algo cambió.
  useEffect(() => {
    if (!listo) {
      return;
    }
    let anterior = '';
    let marco = 0;
    const medir = () => {
      const ancho = window.innerWidth;
      const alto = window.innerHeight;
      const cajas = paso.objetivo().map(cajaDe);
      const union = unirCajas(cajas);
      const foco = union ? ampliarCaja(union, MARGEN_FOCO, ancho, alto) : null;
      const etiquetas = (paso.etiquetas?.() ?? []).map(e => {
        const c = cajaDe(e.el);
        // Centrado sobre su elemento, pero sin salirse por los lados (un botón
        // pegado al borde, como el # de Notas): mitad aproximada del rótulo.
        const mitad = (e.texto.length * 6.5 + 16) / 2;
        return {
          x: Math.min(
            Math.max((c.izq + c.der) / 2, mitad + 6),
            ancho - mitad - 6,
          ),
          y: e.debajo ? c.abajo + 4 : c.arriba - 2,
          debajo: !!e.debajo,
          texto: e.texto,
        };
      });
      const nueva: Medida = {
        ancho,
        alto,
        foco,
        etiquetas,
        altoBocadillo: bocadillo.current?.offsetHeight ?? 170,
      };
      const firma = JSON.stringify(nueva);
      if (firma !== anterior) {
        anterior = firma;
        setMedida(nueva);
      }
      if (paso.toque && brujulaAbierta()) {
        ir(1);
        return;
      }
      marco = window.requestAnimationFrame(medir);
    };
    medir();
    return () => window.cancelAnimationFrame(marco);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listo, indice]);

  useEffect(() => {
    if (listo) {
      document
        .querySelector<HTMLElement>('[data-testid="ayuda-siguiente"]')
        ?.focus({ preventScroll: true });
    }
  }, [listo, indice]);

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        void cerrar();
      } else if (e.key === 'ArrowRight') {
        ir(1);
      } else if (e.key === 'ArrowLeft') {
        ir(-1);
      }
    };
    window.addEventListener('keydown', tecla, true);
    return () => window.removeEventListener('keydown', tecla, true);
  });

  const ancho = medida?.ancho ?? window.innerWidth;
  const alto = medida?.alto ?? window.innerHeight;
  const foco = listo ? (medida?.foco ?? null) : pulsando;
  const radioHueco = paso.radio ?? 14;
  const hueco = `path(evenodd, "${rutaConHueco(foco, radioHueco, ancho, alto)}")`;
  const anchoBoc = Math.min(ANCHO_BOCADILLO, ancho - 28);
  const izqBoc = (ancho - anchoBoc) / 2;
  const altoBoc = medida?.altoBocadillo ?? 170;
  const sitio = colocarBocadillo(foco, altoBoc, alto);
  const centroX = foco ? (foco.izq + foco.der) / 2 : ancho / 2;
  const gaviX = centroX > ancho / 2 ? izqBoc + 16 : izqBoc + anchoBoc - 78;
  const gaviY = sitio.arriba - 52;
  const flecha =
    foco &&
    Math.abs((sitio.debajo ? foco.abajo : foco.arriba) - sitio.arriba) > 0
      ? rutaFlecha(
          [
            izqBoc + anchoBoc * (centroX > ancho / 2 ? 0.55 : 0.45),
            sitio.debajo ? sitio.arriba - 4 : sitio.arriba + altoBoc + 4,
          ],
          [
            Math.max(foco.izq + 14, Math.min(foco.der - 14, centroX)),
            sitio.debajo ? foco.abajo + 4 : foco.arriba - 4,
          ],
        )
      : null;
  const largoFlecha = foco
    ? Math.abs(
        (sitio.debajo ? foco.abajo : foco.arriba) -
          (sitio.debajo ? sitio.arriba : sitio.arriba + altoBoc),
      )
    : 0;
  const { numero, total } = posicionPaso(aplica.current, indice);
  const primero = siguientePaso(aplica.current, indice, -1) === 'fuera';
  const ultimo = siguientePaso(aplica.current, indice, 1) === 'fuera';
  const transicion = quieta ? 'none' : undefined;

  return createPortal(
    <div
      data-testid="ayuda-gavi"
      data-paso={paso.id}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_AYUDA,
        pointerEvents: 'none',
      }}
    >
      {/* Velo borroso con el hueco nítido. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backdropFilter: 'blur(3px) saturate(.8)',
          WebkitBackdropFilter: 'blur(3px) saturate(.8)',
          backgroundColor: 'rgba(27, 58, 85, 0.3)',
          clipPath: hueco,
          transition: transicion ?? `clip-path 550ms ${movimiento.muelle}`,
          animation: `${entra} 250ms ease-out`,
          pointerEvents: 'none',
        }}
      />
      {/* Para los toques: solo deja pasar el dedo por el hueco de la brújula. */}
      <div
        aria-hidden
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'auto',
          clipPath: paso.toque ? hueco : undefined,
        }}
      />
      {foco && (
        <div
          aria-hidden
          data-testid="ayuda-foco"
          style={{
            position: 'absolute',
            left: foco.izq,
            top: foco.arriba,
            width: foco.der - foco.izq,
            height: foco.abajo - foco.arriba,
            borderRadius: radioHueco,
            pointerEvents: 'none',
            boxShadow: `0 0 0 2.5px ${LATON.medio}, 0 0 0 6px rgba(201,151,59,.28), 0 0 22px 4px rgba(242,211,138,.55)`,
            transition: transicion ?? `all 550ms ${movimiento.muelle}`,
            animation: quieta ? 'none' : `${latido} 1.8s ease-in-out infinite`,
          }}
        />
      )}
      {flecha && largoFlecha > 26 && (
        <svg
          key={`flecha-${indice}`}
          aria-hidden
          width={ancho}
          height={alto}
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          <path
            d={flecha.trazo}
            fill="none"
            stroke={LATON.medio}
            strokeWidth={3.2}
            strokeLinecap="round"
            strokeDasharray="7 7"
            style={{
              filter: 'drop-shadow(0 1px 1.5px rgba(14,20,36,.35))',
              animation: quieta ? 'none' : `${dibuja} 700ms ease-out both`,
            }}
          />
          <path
            d={flecha.punta}
            fill={LATON.medio}
            stroke={LATON.oscuro}
            strokeWidth={0.8}
          />
        </svg>
      )}
      {listo && paso.deslizar && <GatilloFantasma />}
      {pulsando && (
        <div
          aria-hidden
          data-testid="ayuda-dedo"
          style={{
            position: 'absolute',
            left: (pulsando.izq + pulsando.der) / 2 - 23,
            top: (pulsando.arriba + pulsando.abajo) / 2 - 23,
            width: 46,
            height: 46,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,.6)',
            border: '2px solid #fff',
            boxShadow: '0 4px 14px rgba(14,20,36,.35)',
            pointerEvents: 'none',
            animation: quieta ? 'none' : `${toque} 1.3s ease-out forwards`,
          }}
        />
      )}
      {listo &&
        medida?.etiquetas.map((e, i) => (
          <div
            key={`${indice}-${e.texto}`}
            aria-hidden
            style={{
              position: 'absolute',
              left: e.x,
              top: e.y,
              transform: e.debajo
                ? 'translate(-50%, 0)'
                : 'translate(-50%, -100%)',
              backgroundColor: LATON.claro,
              color: '#3d2a06',
              fontSize: 11.5,
              fontWeight: 800,
              padding: '3px 8px',
              borderRadius: radio.pildora,
              boxShadow: '0 2px 6px rgba(14,20,36,.25)',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              animation: quieta
                ? 'none'
                : `${aparece} 400ms ${movimiento.muelle} ${250 + i * 250}ms both`,
            }}
          >
            {e.texto}
          </div>
        ))}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: gaviX,
          top: gaviY,
          transition:
            transicion ??
            'left 700ms cubic-bezier(.45,.05,.25,1), top 700ms cubic-bezier(.45,.05,.25,1)',
          transform: centroX < gaviX + 30 ? 'scaleX(-1)' : undefined,
          pointerEvents: 'none',
        }}
      >
        <Gavi
          estado={paso.final || ultimo ? 'aplaude' : 'vuela'}
          clave={indice}
          duracion={paso.final || ultimo ? null : 750}
          ancho={62}
        />
      </div>
      <div
        ref={bocadillo}
        role="dialog"
        aria-modal
        aria-label={t('Gavi explains {{screen}}', { screen: pantalla })}
        data-testid="ayuda-bocadillo"
        style={{
          position: 'absolute',
          left: izqBoc,
          top: sitio.arriba,
          width: anchoBoc,
          boxSizing: 'border-box',
          backgroundColor: color.surface,
          color: color.fg,
          borderRadius: 20,
          boxShadow: `0 14px 34px rgba(14,20,36,.28), 0 0 0 1px rgba(201,151,59,.45)`,
          padding: '14px 14px 12px 16px',
          pointerEvents: 'auto',
          opacity: listo ? 1 : 0,
          transition:
            transicion ?? `top 550ms ${movimiento.muelle}, opacity 200ms`,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: LATON.oscuro,
            marginBottom: 4,
          }}
        >
          <span>{t('Gavi · {{screen}}', { screen: pantalla })}</span>
          <button
            type="button"
            data-testid="ayuda-cerrar"
            aria-label={t('Close help')}
            onClick={() => void cerrar()}
            style={{
              width: 36,
              height: 36,
              margin: '-8px -8px -8px 0',
              borderRadius: '50%',
              border: 0,
              backgroundColor: color.muteSoft,
              color: color.fg2,
              fontSize: 15,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
        <div
          aria-live="polite"
          style={{
            fontSize: 15,
            lineHeight: 1.45,
            fontWeight: 600,
            minHeight: '2.9em',
          }}
        >
          {paso.texto}
        </div>
        {/* Un solo paso (una tarjeta explicada): sin cuerda ni «1 de 1». */}
        <div
          aria-hidden
          style={{
            display: total > 1 ? 'flex' : 'none',
            alignItems: 'center',
            gap: 10,
            marginTop: 10,
          }}
        >
          <Cuerda numero={numero} total={total} />
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: color.fg3,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {t('{{step}} of {{total}}', { step: numero, total })}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 8,
          }}
        >
          <Boton
            variante="fantasma"
            data-testid="ayuda-atras"
            onPress={() =>
              ultimo && otra
                ? otra.onPress()
                : ultimo && !primero
                  ? void cerrar(onPreguntas)
                  : ir(-1)
            }
          >
            {ultimo && otra ? (
              otra.texto
            ) : primero ? (
              <Trans>Questions</Trans>
            ) : ultimo ? (
              <Trans>Another question</Trans>
            ) : (
              <Trans>Back (step)</Trans>
            )}
          </Boton>
          <Boton
            variante={paso.toque ? 'fantasma' : 'primario'}
            data-testid="ayuda-siguiente"
            onPress={() => ir(1)}
          >
            {ultimo ? (
              <Trans>Got it</Trans>
            ) : paso.toque ? (
              <Trans>Tap the compass</Trans>
            ) : (
              <Trans>Next (step)</Trans>
            )}
          </Boton>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Progreso como una cuerda con nudos: hechos en latón, el actual más grande. */
function Cuerda({ numero, total }: { numero: number; total: number }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        height: 18,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 8,
          height: 2,
          borderRadius: 2,
          background:
            'repeating-linear-gradient(90deg, #c8b48d 0 5px, #a99267 5px 7px)',
        }}
      />
      {Array.from({ length: total }, (_, i) => {
        const n = i + 1;
        const ahora = n === numero;
        const hecho = n < numero;
        return (
          <div
            key={n}
            style={{ flex: 1, display: 'flex', justifyContent: 'center' }}
          >
            <span
              data-nudo={ahora ? 'ahora' : hecho ? 'hecho' : 'falta'}
              style={{
                position: 'relative',
                width: ahora ? 13 : 9,
                height: ahora ? 13 : 9,
                borderRadius: '50%',
                backgroundColor: ahora
                  ? LATON.claro
                  : hecho
                    ? LATON.medio
                    : '#e6dcc6',
                border: `1.5px solid ${hecho || ahora ? LATON.oscuro : '#b19c70'}`,
                boxShadow: ahora ? '0 0 0 3px rgba(201,151,59,.25)' : 'none',
                transition: 'all 300ms',
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

type MenuAyudaProps = {
  pantalla: string;
  preguntas: PreguntaAyuda[];
  onElegir: (indice: number) => void;
  onCerrar: () => void;
};

/** «¿Cómo puedo ayudarte?»: la lista con que abre el ojo de buey. */
function MenuAyuda({
  pantalla,
  preguntas,
  onElegir,
  onCerrar,
}: MenuAyudaProps) {
  const { t } = useTranslation();
  const quieta = useReducedMotion();
  const ancho = window.innerWidth;
  const anchoBoc = Math.min(ANCHO_BOCADILLO, ancho - 28);
  const izqBoc = (ancho - anchoBoc) / 2;
  const arriba = Math.max(96, Math.round(window.innerHeight * 0.14));

  useEffect(() => {
    document
      .querySelector<HTMLElement>('[data-testid="ayuda-pregunta"]')
      ?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCerrar();
      }
    };
    window.addEventListener('keydown', tecla, true);
    return () => window.removeEventListener('keydown', tecla, true);
  }, [onCerrar]);

  return createPortal(
    <div
      data-testid="ayuda-gavi"
      data-paso="menu"
      style={{ position: 'fixed', inset: 0, zIndex: Z_AYUDA }}
    >
      <div
        aria-hidden
        onClick={onCerrar}
        style={{
          position: 'absolute',
          inset: 0,
          backdropFilter: 'blur(3px) saturate(.8)',
          WebkitBackdropFilter: 'blur(3px) saturate(.8)',
          backgroundColor: 'rgba(27, 58, 85, 0.3)',
          animation: quieta ? 'none' : `${entra} 250ms ease-out`,
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: izqBoc + anchoBoc - 78,
          top: arriba - 52,
          pointerEvents: 'none',
        }}
      >
        <Gavi estado="vuela" clave={0} duracion={750} ancho={62} />
      </div>
      <div
        role="dialog"
        aria-modal
        aria-label={t('Gavi explains {{screen}}', { screen: pantalla })}
        data-testid="ayuda-bocadillo"
        style={{
          position: 'absolute',
          left: izqBoc,
          top: arriba,
          width: anchoBoc,
          boxSizing: 'border-box',
          backgroundColor: color.surface,
          color: color.fg,
          borderRadius: 20,
          boxShadow: `0 14px 34px rgba(14,20,36,.28), 0 0 0 1px rgba(201,151,59,.45)`,
          padding: '14px 14px 14px 16px',
          animation: quieta ? 'none' : `${entra} 250ms ease-out`,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: LATON.oscuro,
            marginBottom: 4,
          }}
        >
          <span>{t('Gavi · {{screen}}', { screen: pantalla })}</span>
          <button
            type="button"
            data-testid="ayuda-cerrar"
            aria-label={t('Close help')}
            onClick={onCerrar}
            style={{
              width: 36,
              height: 36,
              margin: '-8px -8px -8px 0',
              borderRadius: '50%',
              border: 0,
              backgroundColor: color.muteSoft,
              color: color.fg2,
              fontSize: 15,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: 17, lineHeight: 1.35, fontWeight: 800 }}>
          <Trans>How can I help you?</Trans>
        </div>
        <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
          {preguntas.map((p, i) => (
            <button
              key={p.id}
              type="button"
              data-testid="ayuda-pregunta"
              data-pregunta={p.id}
              onClick={() => onElegir(i)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
                minHeight: 44,
                padding: '10px 14px',
                textAlign: 'left',
                border: 0,
                borderRadius: 12,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: 14.5,
                fontWeight: 700,
                lineHeight: 1.3,
                color: color.fg,
                backgroundColor:
                  i === 0 ? 'rgba(242,211,138,.35)' : color.surface2,
                boxShadow:
                  i === 0
                    ? `inset 0 0 0 1.5px ${LATON.medio}`
                    : `inset 0 0 0 1px ${color.line}`,
                animation: quieta
                  ? 'none'
                  : `${aparece} 350ms ${movimiento.muelle} ${i * 50}ms both`,
              }}
            >
              <span>{p.texto}</span>
              <span
                aria-hidden
                style={{ color: LATON.oscuro, fontSize: 20, lineHeight: 1 }}
              >
                ›
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
